<template>
    <div class="max-w-7xl mx-auto space-y-6 py-6 px-4 sm:px-6 lg:px-8">
      <!-- Page Header -->
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-3">
          <div>
            <h1 class="text-3xl font-bold text-gray-900 dark:text-gray-100">Incident Management Settings</h1>
            <p class="mt-1 text-sm text-gray-600 dark:text-gray-400 dark:text-gray-400">
              Configure incident types, SLA policies, and notification settings
            </p>
          </div>
          <InfoButton title="Incident Management Settings" variant="default">
            <p>
              <strong class="text-secondary-900 dark:text-white">About this page</strong><br>
              The Incident Management Settings page serves as the central configuration hub for your organization's
              incident reporting and response system. This page provides access to three key configuration areas
              that work together to ensure incidents are properly classified, responded to within appropriate
              timeframes, and trigger the right notifications to the right people. Proper configuration of these
              settings is essential for effective incident management, regulatory compliance, and maintaining
              organizational safety standards.
            </p>
            <p>
              <strong class="text-secondary-900 dark:text-white">Available Settings</strong><br>
              • <strong>Incident Types:</strong> Define and organize the categories used to classify incidents
                (Safety, Security, Service & Operations, Environmental, HR & Compliance, Other). Each type
                helps route incidents appropriately and enables trend analysis. Configure type-specific
                attributes including descriptions, categories, and default SLA associations<br>
              • <strong>SLA Policies:</strong> Establish Service Level Agreements that define response time
                and investigation time requirements for different incident types and severity levels. SLAs
                ensure timely triage and resolution while providing accountability through breach tracking
                and reporting. Configure time thresholds, escalation rules, and policy priorities<br>
              • <strong>Notification Policies:</strong> Control who receives email and system notifications
                when incidents are reported, triaged, assigned, or escalated. Configure notification rules
                based on incident type, severity, and resolution path to ensure the right stakeholders are
                informed at the right time without creating notification fatigue
            </p>
            <p>
              <strong class="text-secondary-900 dark:text-white">How to use</strong><br>
              • Click on any of the three setting cards to access that configuration area<br>
              • <strong>Incident Types:</strong> Access to create, edit, activate/deactivate, and organize
                incident classification categories used throughout the system<br>
              • <strong>SLA Policies:</strong> Access to define time-based commitments for incident response
                and resolution, with breach alerts and reporting<br>
              • <strong>Notification Policies:</strong> Access to configure automated notifications that keep
                stakeholders informed without overwhelming them<br>
              • Changes made in these settings apply immediately to new incidents and affect how the entire
                incident management workflow operates<br>
              • Each setting area has its own detailed management interface with create, edit, delete, and
                view capabilities
            </p>
            <p>
              <strong class="text-secondary-900 dark:text-white">Tips</strong><br>
              • <strong>Start with Incident Types</strong> - Define your classification structure before
                configuring SLAs and notifications<br>
              • <strong>Align with organizational structure</strong> - Ensure incident types reflect your
                actual operations and reporting needs<br>
              • <strong>Set realistic SLAs</strong> - Consider team capacity and typical incident complexity
                when defining response and resolution timeframes<br>
              • <strong>Test notification policies</strong> before full deployment - Verify the right people
                get notified at the right times<br>
              • <strong>Review settings periodically</strong> - As your organization evolves, update
                configurations to reflect current needs<br>
              • <strong>Coordinate changes</strong> - When modifying incident types or SLAs, consider the
                impact on existing notification policies<br>
              • <strong>Use categories effectively</strong> - Proper incident categorization enables better
                trend analysis and reporting<br>
              • <strong>Document your configuration</strong> - Keep records of why certain SLA times or
                notification rules were established<br>
              • Changes to SLA policies affect breach calculations for future incidents but don't retroactively
                change existing incident SLAs<br>
              • Only administrators with <strong>incidents.settings.update</strong> permission can modify
                these configurations
            </p>
          </InfoButton>
        </div>
      </div>

      <!-- Settings Cards -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <!-- Incident Types Card -->
        <Link :href="route('settings.incidents.types.index')">
          <Card class="hover:shadow-lg transition-shadow cursor-pointer h-full">
            <div class="p-6">
              <div class="flex items-center space-x-4 mb-4">
                <div class="w-12 h-12 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
                  <svg class="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div class="flex-1 min-w-0">
                  <h3 class="text-lg font-semibold text-gray-900 dark:text-white dark:text-gray-100">Incident Types</h3>
                </div>
                <svg class="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
              <p class="text-sm text-gray-600 dark:text-gray-400">
                Manage incident types and categories to classify and organize safety incidents
              </p>
            </div>
          </Card>
        </Link>

        <!-- Incident SLA Policies Card -->
        <Link :href="route('settings.incidents.sla-policies.index')">
          <Card class="hover:shadow-lg transition-shadow cursor-pointer h-full">
            <div class="p-6">
              <div class="flex items-center space-x-4 mb-4">
                <div class="w-12 h-12 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
                  <svg class="w-6 h-6 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div class="flex-1 min-w-0">
                  <h3 class="text-lg font-semibold text-gray-900 dark:text-white dark:text-gray-100">SLA Policies</h3>
                </div>
                <svg class="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
              <p class="text-sm text-gray-600 dark:text-gray-400">
                Define service level agreements with response and resolution time targets for incidents
              </p>
            </div>
          </Card>
        </Link>

        <!-- Incident Notification Policies Card -->
        <Link :href="route('settings.incidents.notification-policies.index')">
          <Card class="hover:shadow-lg transition-shadow cursor-pointer h-full">
            <div class="p-6">
              <div class="flex items-center space-x-4 mb-4">
                <div class="w-12 h-12 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center flex-shrink-0">
                  <svg class="w-6 h-6 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                </div>
                <div class="flex-1 min-w-0">
                  <h3 class="text-lg font-semibold text-gray-900 dark:text-white dark:text-gray-100">Notification Policies</h3>
                </div>
                <svg class="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
              <p class="text-sm text-gray-600 dark:text-gray-400">
                Configure who gets notified based on incident type, severity, and resolution path
              </p>
            </div>
          </Card>
        </Link>
      </div>
    </div>
</template>

<script setup>
import { Link } from '@inertiajs/vue3';
import Card from '@/Components/Common/Card.vue';
import InfoButton from '@/Components/Common/InfoButton.vue';
import route from '@/Utils/route';
</script>
